import { useTranslator } from "@eo-locale/react";

import {
  Box,
  Button,
  Radio,
  RadioGroup,
  Stack,
  Text
} from "@/storybook-components";
import Alerts from "@/components/alerts";
import useAuthGate from "@/hooks/useAuthGate";
import useProjectPageTheme from "@/hooks/useProjectPageTheme";
import { filterData } from "@/lib";

import {
  showResults,
  updateQuickPoll
} from "@/redux/features/quick-poll/slice";
import {
  fetchQuickPoll,
  saveQuickPoll
} from "@/redux/features/quick-poll/thunk";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface FormProps {
  projectId: string;
  quickPollId: string;
  disabled?: boolean;
}

const Form = ({
  projectId,
  quickPollId,
  disabled = false
}: FormProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { buttonSx } = useProjectPageTheme();
  const { suggestProfileCompletion } = useAuthGate();

  const { answer, status, quickPoll, error, editingResponse } = useAppSelector(
    (state) => state.quickPoll
  );

  const { questionOptions } = quickPoll;

  const handleSubmit = async () => {
    if (disabled) return;

    // Capture before save — fulfilled clears editingResponse.
    const isChangeVote = editingResponse;
    const result = await dispatch(saveQuickPoll({ projectId, quickPollId }));
    if (saveQuickPoll.fulfilled.match(result)) {
      // Refresh tallies for the results view (Response no longer fetches).
      dispatch(fetchQuickPoll({ projectId, quickPollId }));
      // Soft prompt for guests only after change-vote (dismissible once per session).
      if (isChangeVote) {
        suggestProfileCompletion({
          reason: "profileGate.optionalSuggestion",
          source: "quick-poll"
        });
      }
    }
  };

  return (
    <Box
      p={4}
      as="form"
      border="1px"
      borderTop={0}
      borderRadius="sm"
      borderColor="gray.200"
      className="ehq-quickPollFormContainer"
      data-testid="quickPoll-form"
    >
      <RadioGroup
        mb={4}
        disabled={disabled}
        className="ehq-quickPollOptionContainer"
        data-testid="quickPoll-options"
        value={answer.optionId?.toString()}
        onValueChange={(details) => {
          if (disabled) return;
          dispatch(updateQuickPoll(details.value ?? ""));
        }}
      >
        <Stack direction="column">
          {filterData(questionOptions, { deleted: false }).map((option) => (
            <Radio
              key={option.id}
              value={option.id}
              disabled={disabled}
              borderColor="gray.200"
              data-testid={`quickPoll-option-${option.id}`}
            >
              <Text fontFamily="inherit" className="ehq-quickPollOptions">
                {option.name}
              </Text>
            </Radio>
          ))}
        </Stack>
      </RadioGroup>

      {error && status !== "saving" ? (
        <Alerts
          mb={4}
          status="error"
          title={translate("quickPoll.saveError")}
        />
      ) : null}

      <Stack direction={{ base: "column", md: "row" }} gap={3}>
        <Button
          variant="primary"
          loading={status === "saving"}
          disabled={disabled || !answer.optionId}
          onClick={() => void handleSubmit()}
          className="ehq-quickPollSubmitButtton"
          css={buttonSx}
          data-testid="quickPoll-submitButton"
        >
          {translate("common.submit")}
        </Button>
        <Button
          variant="secondary"
          fontFamily="inherit"
          disabled={status === "saving"}
          className="ehq-quickPollResultButton"
          onClick={() => dispatch(showResults())}
          data-testid="quickPoll-viewResultsButton"
        >
          {translate("quickPoll.viewResults")}
        </Button>
      </Stack>
    </Box>
  );
};

export default Form;
